import React, { useMemo } from 'react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts'
import {
  DocumentTextIcon,
  LinkIcon,
  NewspaperIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import { Text, LoadingSpinner } from '../../components/ui'
import type { MarketingClipping } from '../../types/marketing'

interface ClippingDashboardTabProps {
  clippings: MarketingClipping[]
  loading?: boolean
}

function formatMonthLabel(yearMonth: string): string {
  const [year, month] = yearMonth.split('-')
  const date = new Date(Number(year), Number(month) - 1, 1)
  return date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
}

interface KpiCardProps {
  label: string
  value: string
  icon: React.ReactNode
  color: string
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, icon, color }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5 flex items-center gap-4">
    <div className={`flex-shrink-0 h-12 w-12 rounded-lg flex items-center justify-center ${color}`}>
      {icon}
    </div>
    <div className="min-w-0">
      <Text size="xs" color="muted" className="uppercase tracking-wider font-medium">
        {label}
      </Text>
      <p className="text-xl font-bold text-gray-900 dark:text-white truncate">{value}</p>
    </div>
  </div>
)

const EmptyChart: React.FC = () => (
  <div className="h-full flex items-center justify-center">
    <Text size="sm" color="muted">
      Nenhum clipping encontrado.
    </Text>
  </div>
)

const ClippingDashboardTab: React.FC<ClippingDashboardTabProps> = ({
  clippings,
  loading = false,
}) => {
  const totalClippings = clippings.length

  const totalPublicacoes = useMemo(
    () => clippings.reduce((sum, c) => sum + (c.items?.length || 0), 0),
    [clippings]
  )

  const veiculosDistintos = useMemo(() => {
    const ids = new Set<string>()
    clippings.forEach((c) => {
      ;(c.items || []).forEach((item) => {
        if (item.vehicle_id) ids.add(item.vehicle_id)
      })
    })
    return ids.size
  }, [clippings])

  const origemIa = useMemo(
    () => clippings.filter((c) => c.origin === 'ia').length,
    [clippings]
  )

  const origemIaPct =
    totalClippings > 0 ? Math.round((origemIa / totalClippings) * 100) : 0

  const comAnalise = useMemo(
    () => clippings.filter((c) => c.analysis != null).length,
    [clippings]
  )

  const scoreMedio = useMemo(() => {
    const scored = clippings.filter((c) => c.analysis?.relevance_score != null)
    if (scored.length === 0) return null
    const sum = scored.reduce((acc, c) => acc + (c.analysis?.relevance_score || 0), 0)
    return Math.round(sum / scored.length)
  }, [clippings])

  const bySentimentChartData = useMemo(
    () =>
      [
        {
          name: 'Positivo',
          value: clippings.filter((c) => c.analysis?.sentiment === 'positivo').length,
        },
        {
          name: 'Neutro',
          value: clippings.filter((c) => c.analysis?.sentiment === 'neutro').length,
        },
        {
          name: 'Negativo',
          value: clippings.filter((c) => c.analysis?.sentiment === 'negativo').length,
        },
        {
          name: 'Sem análise',
          value: clippings.filter((c) => !c.analysis?.sentiment).length,
        },
      ].filter((row) => row.value > 0),
    [clippings]
  )

  const byMonthChartData = useMemo(() => {
    if (clippings.length === 0) return []

    const monthKeys = new Set<string>()
    clippings.forEach((c) => {
      const key = c.clipping_date?.slice(0, 7)
      if (key) monthKeys.add(key)
    })

    const sortedKeys = Array.from(monthKeys).sort()
    const months = sortedKeys.map((key) => ({
      key,
      name: formatMonthLabel(key),
      count: 0,
    }))
    const monthMap = new Map(months.map((m) => [m.key, m]))

    clippings.forEach((c) => {
      const key = c.clipping_date?.slice(0, 7)
      if (!key) return
      const entry = monthMap.get(key)
      if (entry) entry.count += 1
    })

    return months
  }, [clippings])

  const byVehicleChartData = useMemo(() => {
    const map = new Map<string, { name: string; count: number }>()
    clippings.forEach((c) => {
      ;(c.items || []).forEach((item) => {
        const name = item.vehicle?.name || 'Sem veículo'
        const current = map.get(name) ?? { name, count: 0 }
        current.count += 1
        map.set(name, current)
      })
    })
    return Array.from(map.values()).sort((a, b) => b.count - a.count)
  }, [clippings])

  const byCategoryChartData = useMemo(() => {
    const map = new Map<string, { name: string; count: number }>()
    clippings.forEach((c) => {
      const name = c.category?.name || 'Sem categoria'
      const current = map.get(name) ?? { name, count: 0 }
      current.count += 1
      map.set(name, current)
    })
    return Array.from(map.values()).sort((a, b) => b.count - a.count)
  }, [clippings])

  if (loading) {
    return (
      <div className="py-16 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Total de clippings"
          value={String(totalClippings)}
          icon={<DocumentTextIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />}
          color="bg-primary-50 dark:bg-primary-900/30"
        />
        <KpiCard
          label="Total de publicações"
          value={String(totalPublicacoes)}
          icon={<LinkIcon className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />}
          color="bg-emerald-50 dark:bg-emerald-900/30"
        />
        <KpiCard
          label="Com análise IA"
          value={String(comAnalise)}
          icon={<SparklesIcon className="h-6 w-6 text-violet-600 dark:text-violet-400" />}
          color="bg-violet-50 dark:bg-violet-900/30"
        />
        <KpiCard
          label="Score médio IA"
          value={scoreMedio != null ? String(scoreMedio) : '—'}
          icon={<NewspaperIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />}
          color="bg-amber-50 dark:bg-amber-900/30"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <KpiCard
          label="Veículos distintos"
          value={String(veiculosDistintos)}
          icon={<NewspaperIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />}
          color="bg-amber-50 dark:bg-amber-900/30"
        />
        <KpiCard
          label="Origem IA"
          value={totalClippings > 0 ? `${origemIa} (${origemIaPct}%)` : '0'}
          icon={<SparklesIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />}
          color="bg-purple-50 dark:bg-purple-900/30"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <Text size="lg" className="text-gray-900 dark:text-white">
              Evolução no tempo
            </Text>
            <Text size="sm" color="muted">
              Clippings por mês
            </Text>
          </div>
          <div className="h-64">
            {byMonthChartData.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byMonthChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" name="Clippings" fill="#008B95" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <Text size="lg" className="text-gray-900 dark:text-white">
              Por veículo
            </Text>
            <Text size="sm" color="muted">
              Publicações
            </Text>
          </div>
          <div className="h-64">
            {byVehicleChartData.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byVehicleChartData} layout="vertical" margin={{ left: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={100}
                    tick={{ fontSize: 10 }}
                  />
                  <Tooltip />
                  <Bar dataKey="count" name="Publicações" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <Text size="lg" className="text-gray-900 dark:text-white">
              Por categoria
            </Text>
          </div>
          <div className="h-64">
            {byCategoryChartData.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byCategoryChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10 }}
                    interval={0}
                    angle={-30}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" name="Clippings" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <Text size="lg" className="text-gray-900 dark:text-white">
              Sentimento (IA)
            </Text>
            <Text size="sm" color="muted">
              Análise de clipping
            </Text>
          </div>
          <div className="h-64">
            {bySentimentChartData.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={bySentimentChartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label
                  >
                    {bySentimentChartData.map((_, index) => (
                      <Cell
                        key={`sentiment-${index}`}
                        fill={['#10b981', '#94a3b8', '#ef4444', '#a78bfa'][index % 4]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClippingDashboardTab
